import React, { useState } from 'react';
import { CheckCircle2, Copy, MessageCircle, Radio, Users, AlertTriangle, Route } from 'lucide-react';
import { toast } from 'sonner';
import { waApi, errMsg } from './waApi';

const Stat = ({ label, value, testId }) => (
  <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="text-lg font-semibold text-zinc-100 mt-0.5">{value}</div>
  </div>
);

export const WaStatusCard = ({ status, onRefresh }) => {
  const [routing, setRouting] = useState(false);
  if (!status) return null;
  const connected = status.configured;
  const route = status.meta_route || {};
  const copy = (v, label) => { navigator.clipboard?.writeText(v); toast.success(`${label} copied`); };
  const routeHere = async () => {
    setRouting(true);
    try { await waApi.routeHere(); toast.success('Meta now routes WhatsApp to this server'); onRefresh?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setRouting(false); }
  };
  return (
    <div className="bg-zinc-900 border border-emerald-500/20 rounded-2xl p-5" data-testid="wa-status-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center">
            <MessageCircle size={20} className="text-[#25D366]" />
          </div>
          <div>
            <div className="text-zinc-100 font-semibold">TI on WhatsApp</div>
            <div className="text-xs text-zinc-500">
              Provider: <span className="text-zinc-300 uppercase">{status.provider}</span>
              {status.business_number && <> · Number <span className="text-zinc-300">+{status.business_number}</span>
                {' · '}<a href={`https://wa.me/${status.business_number}?text=hi`} target="_blank" rel="noreferrer" data-testid="wa-open-chat-link"
                  className="text-[#25D366] hover:underline font-semibold">Open chat with TI ↗</a></>}
            </div>
          </div>
        </div>
        <span data-testid="wa-provider-badge"
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${
            connected ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300' : 'bg-amber-500/10 border-amber-500/40 text-amber-300'}`}>
          {connected ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
          {connected ? 'Connected — live replies ON' : 'Simulator mode — add Meta credentials to go live'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Linked users" value={status.linked_users} testId="wa-stat-linked" />
        <Stat label="Messages today" value={status.messages_today} testId="wa-stat-today" />
        <Stat label="Messages total" value={status.messages_total} testId="wa-stat-total" />
        <Stat label="Scheduled pushes" value="08:30 daily · Mon 09:00" testId="wa-stat-crons" />
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-3 text-xs">
        <div className={`bg-zinc-950/60 border rounded-xl p-3 ${connected && route.callback_url && !route.points_here ? 'border-amber-500/50' : 'border-zinc-800'}`}>
          <div className="flex items-center gap-2 text-zinc-300 font-semibold mb-1"><Radio size={12} className="text-emerald-400" /> This server's webhook {status.is_preview && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">PREVIEW · test data</span>}</div>
          <div className="flex items-center gap-2">
            <code className="text-emerald-300 break-all flex-1" data-testid="wa-webhook-url">{status.webhook_url}</code>
            <button onClick={() => copy(status.webhook_url, 'Webhook URL')} data-testid="wa-copy-webhook" className="text-zinc-400 hover:text-zinc-100"><Copy size={13} /></button>
          </div>
          {connected && route.callback_url !== undefined && (
            <div className="mt-2 pt-2 border-t border-zinc-800" data-testid="wa-meta-route">
              <div className="text-zinc-500">Meta currently sends WhatsApp traffic to:</div>
              <code className={`break-all ${route.points_here ? 'text-emerald-300' : 'text-amber-300'}`}>{route.callback_url || '— nothing —'}</code>
              {route.points_here ? (
                <div className="text-emerald-300 mt-1 flex items-center gap-1"><CheckCircle2 size={11} /> TI answers from THIS server's data</div>
              ) : (
                <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-amber-300 flex items-center gap-1"><AlertTriangle size={11} /> Answers come from another server{status.is_preview ? '' : ' (probably preview)'}</span>
                  <button onClick={routeHere} disabled={routing} data-testid="wa-route-here"
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#25D366] text-black font-semibold hover:bg-[#1ebe5a] disabled:opacity-50">
                    <Route size={11} /> {routing ? 'Switching…' : 'Route WhatsApp to this server'}
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="text-zinc-500 mt-1">Verify token = <code>META_VERIFY_TOKEN</code> in backend .env {status.verify_token_set ? '(set ✓)' : '(missing)'}.</div>
        </div>
        <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3">
          <div className="flex items-center gap-2 text-zinc-300 font-semibold mb-1"><Users size={12} className="text-emerald-400" /> Go-live checklist</div>
          <ul className="text-zinc-400 space-y-0.5 list-disc pl-4">
            <li>App Settings → Basic: Privacy Policy URL <code className="text-emerald-300">{window.location.origin}/privacy</code> · Terms URL <code className="text-emerald-300">{window.location.origin}/terms</code> → then switch App Mode to <b>Live</b></li>
            <li>Set <code>META_PHONE_NUMBER_ID</code>, <code>META_ACCESS_TOKEN</code>, <code>META_APP_SECRET</code>, <code>META_WABA_ID</code> in backend .env</li>
            <li>Link each person's number below → they message the TI number</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
